import { AuthContext } from "../context/AuthContext";
import { usePlace } from "../context/PlaceContext";
import { useModal } from "../context/ModalContext";
import React, { useContext, useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Typography,
  Divider,
  Avatar,
  Chip,
  useTheme,
  alpha,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  ElectricCar,
  DirectionsCar,
  Assignment,
  People,
  LocalParking,
  BatteryChargingFull,
  Dashboard,
  Bolt,
  AccountBalanceWallet,
  AdminPanelSettings,
  AccountBalance,
  ExpandLess,
  ExpandMore,
  PersonPin,
  AdminPanelSettingsOutlined,
  ElectricBolt,
  Settings,
  Brightness4,
  Notifications,
  BarChart,
  SwapHoriz,
} from "@mui/icons-material";

const VerticalMenu = ({ isMenuOpen: isMenuOpenProp, toggleMenu: toggleMenuProp }) => {
  const { user } = useContext(AuthContext);
  const { selectedPlace } = usePlace();
  const { openModal } = useModal();
  const isAdminUser = user?.is_admin;
  const [isSubMenuOpen, setIsSubMenuOpen] = useState({
    "Movilidad Eléctrica": true, // Expandido por defecto
    "Finanzas": false,
    "Comunicaciones": false
  });
  const theme = useTheme();
  const location = useLocation();

  // Support controlled (via props) or uncontrolled (internal state) usage.
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = typeof isMenuOpenProp === "boolean";
  const isMenuOpen = isControlled ? isMenuOpenProp : internalOpen;
  const toggleMenu = () => {
    if (isControlled) {
      if (typeof toggleMenuProp === "function") toggleMenuProp();
    } else {
      setInternalOpen((v) => !v);
    }
  };

  const toggleSubMenu = (name) =>
    setIsSubMenuOpen((prevState) => ({
      ...prevState,
      [name]: !prevState[name],
    }));

  // Configuración de menús con iconos
  const MOVILIDAD_ELECTRICA_SUBMENU_ADMIN = [
    { 
      name: "Vehículos registrados", 
      path: "/movilidadElectrica/vehiculosRegistrados",
      icon: <DirectionsCar />,
      description: "Gestión de vehículos"
    },
    { 
      name: "Usuarios registrados", 
      path: "/movilidadElectrica/usuariosRegistrados",
      icon: <People />,
      description: "Gestión de usuarios"
    },
    { 
      name: "Reservas de parqueaderos", 
      path: "/movilidadElectrica/reservasParqueaderos",
      icon: <LocalParking />,
      description: "Gestión de reservas"
    },
    { 
      name: "Matriz de consumos", 
      path: "/movilidadElectrica/matrizConsumos",
      icon: <Bolt />,
      description: "Análisis de consumo"
    },
    { 
      name: "Control Cargadores", 
      path: "/movilidadElectrica/controlCargadores",
      icon: <BatteryChargingFull />,
      description: "Control de cargadores"
    },
    { 
      name: "Gestión de Celdas", 
      path: "/movilidadElectrica/celdas",
      icon: <Dashboard />,
      description: "Gestión de celdas"
    },
    { 
      name: "Tarifa Energética", 
      path: "/tarifa-energetica",
      icon: <AccountBalance />,
      description: "Configuración de tarifas"
    },
    {
      name: "Dashboard",
      path: "/admin/dashboard",
      icon: <BarChart />,
      description: "Consumo e ingresos mensuales"
    },
    {
      name: "Migración a ActionEV",
      path: "/admin/migracion",
      icon: <SwapHoriz />,
      description: "Códigos de migración"
    },
  ];

  const MOVILIDAD_ELECTRICA_SUBMENU_NON_ADMIN = [
    
    { 
      name: "Mis Vehículos", 
      path: "/movilidadElectrica/mis-vehiculos",
      icon: <ElectricCar />,
      description: "Gestionar vehículos"
    },
    { 
      name: "Reservas", 
      path: "/movilidadElectrica/reservas",
      icon: <Assignment />,
      description: "Hacer reservas"
    },
    { 
      name: "Mis reservas", 
      path: "/movilidadElectrica/mis-reservas",
      icon: <PersonPin />,
      description: "Ver mis reservas"
    },
  ];

  const FINANZAS_SUBMENU = isAdminUser
    ? [
        {
          name: "Gestión Financiera",
          path: "/admin/finanzas",
          icon: <AdminPanelSettings />,
          description: "Panel administrativo",
        },
        {
          name: "Todas las Billeteras",
          path: "/admin/billeteras",
          icon: <AccountBalanceWallet />,
          description: "Ver todas las billeteras",
        },
      ]
    : [
        {
          name: "Mi Billetera",
          path: "/mi-billetera",
          icon: <AccountBalanceWallet />,
          description: "Gestionar mi billetera"
        },
        {
          name: "Facturación",
          path: "/facturacion",
          icon: <Assignment />,
          description: "Gestionar datos de facturación"
        }
      ];

  const COMUNICACIONES_SECTION = {
    title: "Comunicaciones",
    icon: <Notifications />,
    items: [
      {
        name: "Comunicado",
        path: "/admin/comunicado",
        icon: <Notifications />,
        description: "Publicar aviso a todos los usuarios"
      }
    ],
    color: "#FF9800"
  };

  const menuSections = [
    {
      title: "Movilidad Eléctrica",
      icon: <ElectricBolt />,
      items: isAdminUser ? MOVILIDAD_ELECTRICA_SUBMENU_ADMIN : MOVILIDAD_ELECTRICA_SUBMENU_NON_ADMIN,
      color: "#4CAF50"
    },
    {
      title: "Finanzas",
      icon: isAdminUser ? <AdminPanelSettingsOutlined /> : <AccountBalanceWallet />,
      items: FINANZAS_SUBMENU,
      color: "#2196F3"
    },
    ...(isAdminUser ? [COMUNICACIONES_SECTION] : [])
  ];

  const drawerWidth = useTheme().breakpoints.down('sm') ? '100vw' : 320;

  const handleGoToSettings = () => {
    toggleMenu();
  };

  return (
    <Drawer
      variant="temporary"
      anchor="left"
      open={isMenuOpen}
      onClose={toggleMenu}
      ModalProps={{
        keepMounted: true, // Better performance on mobile
      }}
      sx={{
        display: 'block',
        zIndex: 1200, // Menor que el zIndex del Navbar (1300)
        '& .MuiDrawer-paper': {
          boxSizing: 'border-box',
          width: { xs: '100vw', sm: '320px' },
          background: 'linear-gradient(180deg, #0B2039 0%, #1A365D 100%)',
          borderRight: 'none',
          boxShadow: '4px 0 20px rgba(0,0,0,0.15)',
          marginTop: '64px', // Espacio para el navbar
          height: 'calc(100vh - 64px)', // Altura ajustada
        },
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header del usuario */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
            backdropFilter: 'blur(10px)',
            padding: { xs: 2, sm: 3 },
            borderBottom: '1px solid rgba(255,255,255,0.1)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Avatar
              sx={{
                width: { xs: 40, sm: 48 },
                height: { xs: 40, sm: 48 },
                background: 'linear-gradient(135deg, #4CAF50 0%, #81C784 100%)',
                fontSize: { xs: '1rem', sm: '1.2rem' },
                fontWeight: 600,
              }}
            >
              {user?.first_name?.charAt(0) || user?.username?.charAt(0) || 'U'}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    color: 'white',
                    fontWeight: 600,
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {user?.first_name || user?.username || sessionStorage.getItem("username") || 'Usuario'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Tooltip title="Configuración">
                    <IconButton
                      component={NavLink}
                      to="/ajustes"
                      onClick={handleGoToSettings}
                      size="small"
                      sx={{
                        color: 'rgba(255,255,255,0.7)',
                        width: { xs: '32px', sm: '36px' },
                        height: { xs: '32px', sm: '36px' },
                        '&:hover': {
                          color: 'white',
                          backgroundColor: 'rgba(255,255,255,0.1)',
                        },
                      }}
                    >
                      <Settings sx={{ fontSize: { xs: '16px', sm: '18px' } }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                <Chip
                  icon={isAdminUser ? <AdminPanelSettings /> : <PersonPin />}
                  label={isAdminUser ? "Admin" : "Usuario"}
                  size="small"
                  sx={{
                    backgroundColor: isAdminUser ? '#FF9800' : '#2196F3',
                    color: 'white',
                    fontSize: '0.7rem',
                    height: '20px',
                    '& .MuiChip-icon': {
                      fontSize: '0.9rem',
                      color: 'white',
                    },
                  }}
                />
              </Box>
            </Box>
          </Box>
          
          {selectedPlace && (
            <Box
              sx={{
                backgroundColor: 'rgba(255,255,255,0.1)',
                borderRadius: '8px',
                padding: '8px 12px',
                border: '1px solid rgba(255,255,255,0.2)',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: 'rgba(255,255,255,0.7)',
                  fontSize: '0.7rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                Ubicación actual
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'white',
                  fontWeight: 500,
                  fontSize: '0.85rem',
                  mt: 0.5,
                }}
              >
                {selectedPlace ? selectedPlace.name : 'No asignada'}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Contenido del menú */}
        <Box sx={{ flex: 1, overflow: 'auto', py: 1 }}>
          <List sx={{ px: 1 }}>
            {menuSections.map((section, sectionIndex) => (
              <Box key={section.title} sx={{ mb: 1 }}>
                {/* Header de sección */}
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => toggleSubMenu(section.title)}
                    sx={{
                      borderRadius: '12px',
                      mb: 1,
                      minHeight: { xs: '48px', sm: '44px' }, // Altura mínima táctil
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      '&:hover': {
                        background: 'rgba(255,255,255,0.1)',
                        transform: 'translateX(4px)',
                      },
                      '&:active': {
                        transform: 'translateX(2px)',
                        background: 'rgba(255,255,255,0.15)',
                      },
                      transition: 'all 0.3s ease-in-out',
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        color: section.color,
                        minWidth: '40px',
                        '& .MuiSvgIcon-root': {
                          fontSize: '1.3rem',
                        },
                      }}
                    >
                      {section.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={section.title}
                      primaryTypographyProps={{
                        fontWeight: 600,
                        fontSize: { xs: '1rem', sm: '0.95rem' }, // Texto más grande en móvil
                        color: 'white',
                      }}
                    />
                    {isSubMenuOpen[section.title] ? (
                      <ExpandLess sx={{ color: 'rgba(255,255,255,0.7)' }} />
                    ) : (
                      <ExpandMore sx={{ color: 'rgba(255,255,255,0.7)' }} />
                    )}
                  </ListItemButton>
                </ListItem>

                {/* Items de la sección */}
                <Collapse in={isSubMenuOpen[section.title]} timeout="auto" unmountOnExit>
                  <List disablePadding sx={{ pl: 1 }}>
                    {section.items.map((item, itemIndex) => {
                      const isActive = location.pathname === item.path;
                      return (
                        <ListItem key={itemIndex} disablePadding sx={{ mb: 0.5 }}>
                          <ListItemButton
                            component={NavLink}
                            to={item.path}
                            onClick={toggleMenu}
                            sx={{
                              borderRadius: '10px',
                              minHeight: { xs: '52px', sm: '48px' }, // Más alto en móvil para mejor toque
                              backgroundColor: isActive 
                                ? 'rgba(255,255,255,0.15)' 
                                : 'transparent',
                              border: isActive 
                                ? '1px solid rgba(255,255,255,0.3)' 
                                : '1px solid transparent',
                              px: { xs: 2, sm: 1.5 }, // Más padding horizontal en móvil
                              '&:hover': {
                                backgroundColor: 'rgba(255,255,255,0.1)',
                                transform: 'translateX(8px)',
                                border: '1px solid rgba(255,255,255,0.2)',
                              },
                              '&:active': {
                                backgroundColor: 'rgba(255,255,255,0.2)',
                                transform: 'translateX(4px)',
                              },
                              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                              position: 'relative',
                              overflow: 'hidden',
                              '&::before': {
                                content: '""',
                                position: 'absolute',
                                left: 0,
                                top: 0,
                                height: '100%',
                                width: isActive ? '4px' : '0px',
                                backgroundColor: section.color,
                                transition: 'width 0.3s ease-in-out',
                              },
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                color: isActive ? section.color : 'rgba(255,255,255,0.7)',
                                minWidth: '36px',
                                transition: 'color 0.3s ease-in-out',
                                '& .MuiSvgIcon-root': {
                                  fontSize: '1.1rem',
                                },
                              }}
                            >
                              {item.icon}
                            </ListItemIcon>
                            <ListItemText
                              primary={item.name}
                              secondary={item.description}
                              primaryTypographyProps={{
                                fontWeight: isActive ? 600 : 400,
                                fontSize: { xs: '0.9rem', sm: '0.85rem' }, // Texto más grande en móvil
                                color: isActive ? 'white' : 'rgba(255,255,255,0.9)',
                                transition: 'all 0.3s ease-in-out',
                              }}
                              secondaryTypographyProps={{
                                fontSize: { xs: '0.75rem', sm: '0.7rem' },
                                color: 'rgba(255,255,255,0.5)',
                                mt: 0.2,
                                display: { xs: 'none', sm: 'block' }, // Ocultar descripción en móvil para ahorrar espacio
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </List>
                </Collapse>
                
                {sectionIndex < menuSections.length - 1 && (
                  <Divider 
                    sx={{ 
                      my: 2, 
                      backgroundColor: 'rgba(255,255,255,0.1)',
                      mx: 1,
                    }} 
                  />
                )}
              </Box>
            ))}
          </List>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            padding: 2,
            borderTop: '1px solid rgba(255,255,255,0.1)',
            background: 'rgba(0,0,0,0.2)',
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: 'rgba(255,255,255,0.5)',
              textAlign: 'center',
              display: 'block',
              fontSize: '0.7rem',
            }}
          >
            ComHub v1.0
          </Typography>
        </Box>
      </Box>
    </Drawer>
  );
};

export default VerticalMenu;
